import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/app/AuthContext";
import StoreButtons from "./StoreButtons";

const HERO_IMG =
  "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY29ycG9yYXRlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NTgxODkyMXww&ixlib=rb-4.1.0&q=85";

const lines = ["BRING A", "DECISION.", "LEAVE WITH A PLAN."];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { guest } = useAuth();
  const [trying, setTrying] = useState(false);

  const tryNow = async () => {
    if (trying) return;
    setTrying(true);
    try {
      await guest();
      navigate("/app");
    } finally {
      setTrying(false);
    }
  };

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden"
    >
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img
          src={HERO_IMG}
          alt="Minimalist corporate architecture"
          className="w-full h-full object-cover grayscale contrast-125"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0E0D0B] via-transparent to-black/40" />

      <motion.div
        style={{ y: textY }}
        className="relative z-10 w-full px-6 md:px-12 pb-16 md:pb-24 pt-40"
      >
        <div className="overflow-hidden mb-8">
          <motion.p
            custom={0}
            variants={lineVariants}
            initial="hidden"
            animate="visible"
            className="font-mono-label text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#C9B896]/70"
          >
            Boardroom — A private decision boardroom. iOS & Android
          </motion.p>
        </div>

        <h1 className="font-serif-display font-light tracking-tighter leading-[0.95] text-[13vw] md:text-[9vw] text-white">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i + 1}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === 2 ? "italic text-white/90" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-5 text-sm md:text-base leading-relaxed text-stone-400 max-w-md"
          >
            Boardroom is a case-management workspace for real decisions.
            Choose an advisor. Answer three to five sharp questions. Get an
            analysis, an advisory board, three realistic paths, and an action
            plan you can actually follow.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-7"
          >
            <button
              data-testid="hero-try-now-btn"
              onClick={tryNow}
              disabled={trying}
              className="group inline-flex items-center gap-4 bg-[#C9B896] text-[#0E0D0B] rounded-full pl-8 pr-4 py-4 mb-8 hover:bg-white transition-colors duration-300 disabled:opacity-60"
            >
              <span className="font-mono-label text-[11px] tracking-[0.25em] uppercase">
                {trying ? "Taking your seat…" : "Try it now — no signup needed"}
              </span>
              <span className="w-10 h-10 rounded-full bg-[#0E0D0B]/10 flex items-center justify-center">
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" strokeWidth={1.5} />
              </span>
            </button>
            <StoreButtons testidPrefix="hero" compact />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
